/**
 * Hook to check user's trial status
 * Returns trial info from organization data
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';

interface TrialStatus {
  isTrialing: boolean;
  isExpired: boolean;
  daysRemaining: number | null;
  trialEndsAt: Date | null;
  plan: string;
  planStatus: string;
  hasActiveSubscription: boolean;
  isLoading: boolean;
}

export function useTrialStatus(): TrialStatus {
  const { user } = useAuth();
  const [status, setStatus] = useState<TrialStatus>({
    isTrialing: false,
    isExpired: false,
    daysRemaining: null,
    trialEndsAt: null,
    plan: 'free',
    planStatus: 'active',
    hasActiveSubscription: false,
    isLoading: true,
  });

  useEffect(() => {
    if (!user) {
      setStatus({
        isTrialing: false,
        isExpired: false,
        daysRemaining: null,
        trialEndsAt: null,
        plan: 'free',
        planStatus: 'active',
        hasActiveSubscription: false,
        isLoading: false,
      });
      return;
    }

    const checkTrialStatus = async () => {
      try {
        // Get user's organization ID first
        const { data: orgMember, error: memberError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();

        if (memberError) throw memberError;

        if (!orgMember) {
          throw new Error('No organization found');
        }

        // Then get the organization details
        const { data: org, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', orgMember.organization_id)
          .single();

        if (orgError) throw orgError;

        if (!org) {
          throw new Error('Organization not found');
        }

        const trialEndsAt = org.trial_ends_at ? new Date(org.trial_ends_at) : null;
        const now = new Date();
        const isTrialing = org.plan_status === 'trialing';
        const isExpired = trialEndsAt ? now > trialEndsAt : false;

        let daysRemaining: number | null = null;
        if (trialEndsAt) {
          const diffTime = trialEndsAt.getTime() - now.getTime();
          const diffHours = diffTime / (1000 * 60 * 60);

          // If less than 24 hours, show as 0 days (today)
          // Otherwise round up to next full day
          if (diffHours < 24) {
            daysRemaining = 0;
          } else {
            daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          }
          daysRemaining = Math.max(0, daysRemaining); // Don't show negative days
        }

        // Check if user has an active subscription (from Stripe webhook synced data)
        const { data: subscriptions } = await supabase
          .from('subscriptions')
          .select('status')
          .eq('user_id', user.id);

        const hasActiveSubscription = subscriptions && subscriptions.length > 0 && (subscriptions[0]?.status === 'active' || subscriptions[0]?.status === 'trialing');

        setStatus({
          isTrialing,
          isExpired: isExpired && !hasActiveSubscription,
          daysRemaining,
          trialEndsAt,
          plan: org.plan,
          planStatus: org.plan_status,
          hasActiveSubscription,
          isLoading: false,
        });
      } catch (error) {
        console.error('Error checking trial status:', error);
        setStatus((prev) => ({ ...prev, isLoading: false }));
      }
    };

    checkTrialStatus();
  }, [user]);

  return status;
}
